import { icons, SmartButton } from '@tunasong/icons'
import type { SvgIconComponent } from '@tunasong/icons'
import type { Agent, Persisted } from '@tunasong/schemas'
import { resolveDefaultColor, resolveDefaultIconKey } from './role-defaults.js'

/**
 * Resolve the `SvgIconComponent` to use for an agent.
 *
 * Order:
 *  1. `agent.iconKey` (when set and a valid key in the `@tunasong/icons` registry)
 *  2. role-keyed default (see {@link resolveDefaultIconKey})
 *  3. generic fallback (`SmartButton`) — used when both the explicit key and
 *     the role-default key are absent from the registry. The registry is the
 *     single source of truth; the schema layer cannot enforce this because the
 *     registry isn't statically known there.
 *
 * The lookup is a plain map read; no hooks, no fetches — safe to call inside
 * render bodies or memoised callbacks.
 */
export const resolveAgentIcon = (agent: Persisted<Agent> | Agent): SvgIconComponent => {
  const key = resolveDefaultIconKey(agent)
  const candidate = icons[key as keyof typeof icons]
  if (candidate) {
    return candidate
  }
  // Unknown key — fall back to the generic icon so the surface still renders.
  return SmartButton
}

export interface AgentVisuals {
  color: string
  Icon: SvgIconComponent
}

/**
 * Bundled `{ color, Icon }` lookup. Surfaces that need both — rail card,
 * sidebar tab, palette command, chat avatar — call this once and avoid
 * resolving the role twice.
 */
export const resolveAgentVisuals = (agent: Persisted<Agent> | Agent): AgentVisuals => ({
  color: resolveDefaultColor(agent),
  Icon: resolveAgentIcon(agent),
})
